import type { CSSProperties, ReactNode } from 'react';

import { Popover, Space, Tag, Typography } from '@arco-design/web-react';

/** 通用折叠标签组件的尺寸类型。 */
type CollapsedTagListSize = 'small' | 'default';

/** 通用折叠标签组件属性。 */
export interface CollapsedTagListProps {
  /** 标签值列表。 */
  items?: (string | number)[];
  /** 无标签值时展示的兜底标签文案。 */
  emptyTagLabel?: ReactNode;
  /** 直接展示的标签数量上限。 */
  maxVisible?: number;
  /** 标签颜色。 */
  color?: string;
  /** 空值兜底标签颜色，默认沿用标签颜色。 */
  emptyColor?: string;
  /** 折叠数量标签颜色，默认沿用标签颜色。 */
  overflowColor?: string;
  /** 标签尺寸。 */
  size?: CollapsedTagListSize;
  /** 折叠提示浮层标题。 */
  popoverTitle?: ReactNode;
  /** 外层容器样式。 */
  style?: CSSProperties;
  /** 自定义折叠标签文案。 */
  renderOverflowLabel?: (hiddenCount: number, totalCount: number) => ReactNode;
  /** 自定义单个标签渲染。 */
  renderTag?: (value: string | number, defaultTagNode: ReactNode) => ReactNode;
  /** 自定义折叠浮层内的标签渲染，默认沿用 `renderTag`。 */
  renderPopoverTag?: (value: string | number, defaultTagNode: ReactNode) => ReactNode;
}

/** 默认直出标签数量。 */
const DEFAULT_MAX_VISIBLE = 1;

/** 渲染单个标签节点。 */
function renderTagNode(
  value: string | number,
  color: string,
  size: CollapsedTagListSize,
  renderTag?: (value: string | number, defaultTagNode: ReactNode) => ReactNode,
) {
  /** 默认的 Tag 节点。 */
  const defaultTagNode = (
    <Tag key={String(value)} color={color} size={size}>
      {value}
    </Tag>
  );

  return renderTag ? renderTag(value, defaultTagNode) : defaultTagNode;
}

/** 通用折叠标签列表组件。 */
export function CollapsedTagList(props: CollapsedTagListProps) {
  /** 原始标签值列表。 */
  const items = Array.isArray(props.items) ? props.items : [];
  /** 无标签值时的兜底文案。 */
  const emptyTagLabel = props.emptyTagLabel;
  /** 直出标签数量上限。 */
  const maxVisible = props.maxVisible ?? DEFAULT_MAX_VISIBLE;
  /** 标签颜色。 */
  const color = props.color || 'blue';
  /** 空值兜底标签颜色。 */
  const emptyColor = props.emptyColor || color;
  /** 折叠数量标签颜色。 */
  const overflowColor = props.overflowColor || color;
  /** 标签尺寸。 */
  const size = props.size || 'default';
  /** 外层容器样式。 */
  const style = props.style;
  /** 自定义折叠标签渲染函数。 */
  const renderOverflowLabel = props.renderOverflowLabel;
  /** 浮层标题。 */
  const popoverTitle = props.popoverTitle;
  /** 自定义标签渲染函数。 */
  const renderTag = props.renderTag;
  /** 自定义折叠浮层标签渲染函数。 */
  const renderPopoverTag = props.renderPopoverTag || renderTag;
  /** 直接展示的标签列表。 */
  const visibleItems = items.slice(0, maxVisible);
  /** 被折叠的标签列表。 */
  const hiddenItems = items.slice(maxVisible);
  /** 被折叠的标签数量。 */
  const hiddenCount = hiddenItems.length;

  if (!items.length) {
    if (emptyTagLabel === undefined || emptyTagLabel === null) {
      return null;
    }

    return (
      <Space align="center" wrap size={[8, 8]} style={style}>
        <Tag color={emptyColor} size={size}>
          {emptyTagLabel}
        </Tag>
      </Space>
    );
  }

  return (
    <Space align="center" size={[8, 8]} style={style}>
      {visibleItems.map((item) => (
        <span key={String(item)} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderTagNode(item, color, size, renderTag)}
        </span>
      ))}
      {hiddenCount > 0 ? (
        <Popover
          position="top"
          content={
            <div style={{ maxWidth: 420 }}>
              {popoverTitle ? (
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  {popoverTitle}
                </Typography.Text>
              ) : null}
              <Space align="center" wrap size={[8, 8]}>
                {items.map((item) => (
                  <span key={String(item)} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {renderTagNode(item, color, size, renderPopoverTag)}
                  </span>
                ))}
              </Space>
            </div>
          }
        >
          <Tag color={overflowColor} size={size} style={{ cursor: 'pointer' }}>
            {renderOverflowLabel ? renderOverflowLabel(hiddenCount, items.length) : `+${hiddenCount}`}
          </Tag>
        </Popover>
      ) : null}
    </Space>
  );
}
